import { createContext, useContext, type ReactNode } from "react";
import { apiFetch } from "@/api/fetch";

/**
 * Shape returned by `GET /api/organizations/{slug}`. Mirrors
 * Javeed.Shared.Organizations.OrganizationDto. Defined locally for now; will
 * be replaced by the generated openapi-ts type once `npm run gen:api` lands.
 */
export interface OrganizationDto {
  id: string;
  slug: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  memberCount: number;
  activeApiKeyCount: number;
  companyCount: number;
}

/**
 * Query key + fetcher exposed as one record so it's trivial to pass to
 * `queryClient.ensureQueryData` from a route's beforeLoad. Co-locating the key
 * and fetcher keeps every consumer (loader, dashboard cards, sidebar) in lock
 * step — a typo in one place can't desync from a stale read in another.
 */
export const orgQuery = (slug: string) => ({
  queryKey: ["organization", slug] as const,
  queryFn: () => apiFetch<OrganizationDto>(`/api/organizations/${slug}`),
});

/**
 * localStorage key used to remember which org the user opened last. Read by the
 * `/` route to redirect straight into that workspace on next launch; written by
 * the `/o/$slug` layout whenever the user actually loads an org page. Falls
 * back to the first org in the user's landing payload when no cache exists or
 * the cached slug is no longer accessible.
 */
const LAST_ORG_SLUG_KEY = "javeed.lastOrgSlug";

export function getLastOrgSlug(): string | null {
  try {
    return window.localStorage.getItem(LAST_ORG_SLUG_KEY);
  } catch {
    return null;
  }
}

export function setLastOrgSlug(slug: string): void {
  try {
    window.localStorage.setItem(LAST_ORG_SLUG_KEY, slug);
  } catch {
    /* noop */
  }
}

export function clearLastOrgSlug(): void {
  try {
    window.localStorage.removeItem(LAST_ORG_SLUG_KEY);
  } catch {
    /* noop */
  }
}

const OrgContext = createContext<OrganizationDto | null>(null);

export function OrgProvider({ value, children }: { value: OrganizationDto; children: ReactNode }) {
  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

/**
 * Read the current organization from context. Throws if called outside an
 * <OrgProvider> — every `/o/$slug/*` route is wrapped by the workspace layout
 * which provides this, so a thrown error means a routing bug, not a runtime
 * edge case to silently handle.
 */
export function useOrg(): OrganizationDto {
  const ctx = useContext(OrgContext);
  if (!ctx) {
    throw new Error("useOrg() called outside of <OrgProvider>. Mount under /o/$slug/* routes only.");
  }
  return ctx;
}
